import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ProfileHeader from './components/ProfileHeader';
import ProfileTabs from './components/ProfileTabs';
import { userService } from '../../services/userService';
import { UserProfile } from '../../types/api';
import { buildOwnProfilePath, buildProfilePath } from '../../utils/share';

interface ProfileProps {
    userIdOverride?: string;
    forcePublicProfile?: boolean;
}

const Profile: React.FC<ProfileProps> = ({ userIdOverride, forcePublicProfile = false }) => {
    const { id: routeId } = useParams<{ id: string }>();
    const id = userIdOverride || routeId;
    const location = useLocation();
    const navigate = useNavigate();
    const isMeRoute = location.pathname.replace(/\/+$/, '').endsWith('/me');
    const isOwnProfile = !forcePublicProfile && (!id || isMeRoute);

    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    // Single source of truth: fetch profile once at the top level
    useEffect(() => {
        let isActive = true;

        const withCurrentLocationState = (path: string) => `${path}${location.search}${location.hash}`;
        const navigateToOwnProfile = (userId: string) => {
            navigate(withCurrentLocationState(buildOwnProfilePath(userId)), { replace: true });
        };
        const navigateToPublicProfile = (userId: string) => {
            navigate(withCurrentLocationState(buildProfilePath(userId)), { replace: true });
        };

        const fetchProfile = async () => {
            setLoading(true);
            setProfile(null);

            try {
                if (!id) {
                    const response = await userService.getUserProfile();
                    if (response.success && response.data?.userProfile) {
                        navigateToOwnProfile(response.data.userProfile.userId);
                    }
                    return;
                }

                if (!forcePublicProfile && isMeRoute) {
                    const response = await userService.getUserProfile();
                    const currentProfile = response.success ? response.data?.userProfile : undefined;

                    if (!currentProfile) {
                        navigateToPublicProfile(id);
                        return;
                    }

                    if (currentProfile.userId !== id) {
                        navigateToPublicProfile(id);
                        return;
                    }

                    if (isActive) {
                        setProfile(currentProfile);
                    }
                    return;
                }

                const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
                const [currentResponse, profileResponse] = await Promise.all([
                    token ? userService.getUserProfile({ suppressAuthPrompt: true }) : Promise.resolve(null),
                    userService.getUserProfileById(id)
                ]);

                const currentProfile = currentResponse?.success ? currentResponse.data?.userProfile : undefined;
                if (!forcePublicProfile && currentProfile?.userId === id) {
                    navigateToOwnProfile(id);
                    return;
                }

                if (profileResponse.success && profileResponse.data?.userProfile && isActive) {
                    setProfile(profileResponse.data.userProfile);
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        fetchProfile();

        return () => {
            isActive = false;
        };
    }, [forcePublicProfile, id, isMeRoute, location.hash, location.search, navigate]);

    const viewUserId = profile?.userId || id || '';

    // Single user info object to pass down to child components
    const userInfo = useMemo(() => {
        if (!profile) return undefined;
        return {
            id: profile.userId,
            name: profile.displayName || profile.username || profile.userId,
            avatar: profile.avatarUrl || ''
        };
    }, [profile]);

    return (
        <div className="min-h-screen bg-white">
            {/* Container to center content and add top padding for fixed header */}
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-24 pb-20">
                <ProfileHeader
                    isOwnProfile={isOwnProfile}
                    userId={viewUserId}
                    profile={profile}
                    loading={loading}
                />

                <ProfileTabs isOwnProfile={isOwnProfile} userInfo={userInfo} />
            </div>
        </div>
    );
};

export default Profile;
